// A vehicle's temperature sensors: each sensor's newest reading (with "too warm / too cold" in
// words, never colour alone) and a chart of the last 24 hours or 7 days with the allowed range.
// Shown on Sprout's vehicle page and in the live summary of both apps; nothing when the vehicle
// has no temperature inputs. Fridges also get their on/off state and setpoints here (the
// chart shows only the temperatures). Copied in web/ and crm/.
import { useEffect, useState } from 'react';
import { api, FridgeState, TemperatureSeries } from '../api';
import { ago, dateTime, dayLabel, time } from '../format';
import { icons } from './Icons';
import { TimeChart } from './TimeChart';

const RANGES = [['24h', 'Last 24 h', 24], ['7d', '7 days', 24 * 7]] as const;
type Range = (typeof RANGES)[number][0];
const STALE_MS = 3 * 3600_000;
const deg = (v: number) => v.toLocaleString('en-IE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

const fridgeWord = (f: FridgeState) => (f.on === null ? 'Fridge: no reading' : f.on ? 'Fridge on' : 'Fridge off');

// The small snowflake on the live list: solid when the fridge runs, crossed out when off.
export function FridgeMark({ fridge }: { fridge: FridgeState | null }) {
  if (!fridge) return null;
  const word = fridgeWord(fridge);
  const state = fridge.on === null ? 'none' : fridge.on ? 'on' : 'off';
  return <span className={`fridge-mark fridge-${state}`} role="img" aria-label={word} title={word}>{icons.snowflake}</span>;
}

function sinceText(iso: string) {
  const today = new Date().toDateString() === new Date(iso).toDateString();
  return today ? time(iso) : `${dayLabel(iso)} ${time(iso)}`;
}

export function Temperatures({ vehicleId, compact = false, refreshKey }: { vehicleId: number; compact?: boolean; refreshKey?: unknown }) {
  const [range, setRange] = useState<Range>('24h');
  const [data, setData] = useState<{ sensors: TemperatureSeries[]; fridge: FridgeState | null; from: number; to: number } | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let live = true;
    const hours = RANGES.find((r) => r[0] === range)![2];
    const to = Date.now();
    const from = to - hours * 3600_000;
    api.temperatures(vehicleId, new Date(from).toISOString(), new Date(to).toISOString())
      .then((r) => { if (live) { setData({ ...r, from, to }); setError(''); } })
      .catch((e) => live && setError((e as Error).message));
    return () => { live = false; };
  }, [vehicleId, range, refreshKey]);

  if (error) return <p className="error small">{error}</p>;
  if (!data || (data.sensors.length === 0 && !data.fridge)) return null;
  const { fridge } = data;
  const temps = data.sensors.filter((s) => s.kind !== 'setpoint');
  const setpoints = data.sensors.filter((s) => s.kind === 'setpoint');
  const unit = temps[0]?.unit ?? '°C';
  // The chart has one axis, so it can show one allowed range: the one the sensors with a range share.
  const ranges = [...new Set(temps.filter((s) => s.limits).map((s) => JSON.stringify(s.limits)))];
  const band = ranges.length === 1 ? temps.find((s) => s.limits)!.limits : null;

  const body = (
    <>
      {fridge && (
        <p className={`fridge-state fridge-${fridge.on === null ? 'none' : fridge.on ? 'on' : 'off'}`}>
          <span className="fridge-mark" aria-hidden="true">{icons.snowflake}</span>
          <b>{fridgeWord(fridge)}</b>
          {fridge.on !== null && fridge.since && <span className="muted"> · since {sinceText(fridge.since)}</span>}
          {fridge.at && (fridge.on === null || Date.now() - new Date(fridge.at).getTime() > STALE_MS) && <span className="muted"> · last reading {ago(fridge.at)}</span>}
        </p>
      )}
      {setpoints.length > 0 && (
        <ul className="temps">
          {setpoints.map((s) => {
            const last = s.points[s.points.length - 1];
            const changed = s.low !== null && s.high !== null && s.low !== s.high;
            return (
              <li key={s.name} className="temp temp-setpoint">
                <span className="temp-name">Setpoint {s.name}</span>
                <span className="temp-value">{last ? `${deg(last[1])} ${s.unit}` : '–'}</span>
                <span className="temp-note">
                  {last ? <span title={dateTime(last[0])}>{ago(new Date(last[0]).toISOString())}</span> : 'no reading in this period'}
                  {changed && <> · was {deg(s.low!)} to {deg(s.high!)} {s.unit} in this period</>}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {temps.length > 0 && <ul className="temps">
        {temps.map((s) => {
          const last = s.points[s.points.length - 1];
          const stale = !last || Date.now() - last[0] > STALE_MS;
          const v = last?.[1];
          const state = v === undefined ? 'none' : s.limits?.min !== undefined && v < s.limits.min ? 'low' : s.limits?.max !== undefined && v > s.limits.max ? 'high' : 'ok';
          return (
            <li key={s.name} className={`temp temp-${state}`}>
              <span className="temp-name">{s.name}</span>
              <span className="temp-value">{v === undefined ? '–' : `${v.toLocaleString('en-IE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} ${s.unit}`}</span>
              <span className="temp-note">
                {state === 'high' && <b>Too warm · </b>}
                {state === 'low' && <b>Too cold · </b>}
                {last ? <span title={dateTime(last[0])}>{stale ? `last reading ${ago(new Date(last[0]).toISOString())}` : ago(new Date(last[0]).toISOString())}</span> : 'no reading in this period'}
                {s.limits && <> · allowed {s.limits.min ?? '…'} to {s.limits.max ?? '…'} {s.unit}</>}
                {s.outside > 0 && <> · {s.outside} reading{s.outside === 1 ? '' : 's'} outside</>}
              </span>
            </li>
          );
        })}
      </ul>}
      <div className="seg" role="group" aria-label="Period">
        {RANGES.map(([k, label]) => (
          <button key={k} type="button" aria-pressed={range === k} onClick={() => setRange(k)}>{label}</button>
        ))}
      </div>
      {temps.length > 0 && <TimeChart title={temps.length === 1 ? `${temps[0].name} temperature` : 'Temperatures'} unit={unit}
        series={temps.map((s) => ({ name: s.name, points: s.points, ranged: !!band && JSON.stringify(s.limits) === JSON.stringify(band) }))} from={data.from} to={data.to}
        limits={band} empty="No temperature readings in this period." />}
    </>
  );
  const heading = fridge || setpoints.length ? 'Fridge and temperatures' : 'Temperatures';
  return compact
    ? <section className="temps-box compact" aria-label="Temperatures"><h2 className="vsum-h">{heading}</h2>{body}</section>
    : <section className="card form temps-box" aria-label="Temperatures"><div className="page-head"><h2>{heading}</h2></div>{body}</section>;
}
